import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const FIRST_CHECK_MS = 1000
const BACKOFF = 1.5
const MAX_INTERVAL_MS = 10000

// The second way a job's result reaches a page that is waiting for it. The job
// broadcasts a Turbo Stream when it finishes, but that broadcast rides the web
// process's solid_cable listener thread, and when the thread dies nothing else
// notices -- the page just sits there. It is also missed by a job that finishes
// before the browser has subscribed. So this asks the URL instead: 202 means
// "still working", and anything else that succeeds is the same stream the
// broadcast would have carried, rendered here. Whichever lands first replaces
// the element this controller sits on, and its disconnect stops the polling.
//
// Registered in *both* bundles (controllers/index.js and portal.js) from one
// file, like `toast`: the download page needs it on both surfaces, and the
// markup each one renders is its own.
//
// Unlike background_search_poll this never gives up. A build can legitimately
// take minutes, and the member is emailed either way; a page that keeps asking
// every ten seconds is cheaper than one that tells them to refresh.
export default class extends Controller {
  static values = { url: String }

  connect() {
    this.interval = FIRST_CHECK_MS
    this.schedule()
  }

  disconnect() {
    clearTimeout(this.timer)
    this.timer = null
  }

  schedule() {
    this.timer = setTimeout(() => this.check(), this.interval)
    this.interval = Math.min(this.interval * BACKOFF, MAX_INTERVAL_MS)
  }

  async check() {
    let response
    try {
      response = await fetch(this.urlValue, {
        credentials: "same-origin",
        headers: { Accept: "text/vnd.turbo-stream.html" }
      })
    } catch {
      // A network blip is not an answer -- ask again.
    }
    if (!this.timer) return // replaced while the request was in flight

    if (response && (response.redirected || response.status === 401)) {
      // Signed out while waiting: the poll followed the redirect to sign-in.
      // Send the whole page there rather than rendering nothing.
      window.location.reload()
    } else if (!response || response.status === 202 || response.status >= 500) {
      this.schedule()
    } else if (response.ok) {
      Turbo.renderStreamMessage(await response.text())
    }
  }
}
